import { useCallback, useEffect, useRef, useState } from "react";

export default function useAudioRecorder({ onStatus, captureProfile = "legacy" }) {
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [uploadedAudio, setUploadedAudio] = useState(null);
  const fileInputRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const sessionRef = useRef(null);
  const timerRef = useRef(null);
  const generationRef = useRef(0);
  const requestingRef = useRef(false);
  const statusRef = useRef(onStatus);
  statusRef.current = onStatus;

  const release = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const dispose = useCallback(() => {
    generationRef.current += 1;
    const recorder = recorderRef.current;
    if (recorder) {
      recorder.onstop = null;
      recorder.ondataavailable = null;
      if (recorder.state !== "inactive") recorder.stop();
    }
    // Settle any consumers waiting for a recording that is being discarded.
    sessionRef.current?.resolve(null);
    sessionRef.current = null;
    recorderRef.current = null;
    release();
  }, [release]);

  useEffect(() => dispose, [dispose]);

  const reset = useCallback(() => {
    dispose();
    setRecording(false);
    setRecorded(false);
    setRecordTime(0);
    setAudioBlob(null);
    setUploadedAudio(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [dispose]);

  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setRecordTime((time) => time + 1), 1000);
  };

  const handleRecordClick = async () => {
    if (requestingRef.current || (sessionRef.current?.stopping && !sessionRef.current?.blob)) return;
    const generation = generationRef.current;
    try {
      const recorder = recorderRef.current;
      if (recorder?.state === "recording") {
        recorder.pause();
        clearInterval(timerRef.current);
        setRecording(false);
        statusRef.current("Recording paused...");
        return;
      }
      if (recorder?.state === "paused") {
        recorder.resume();
        startTimer();
        setRecording(true);
        statusRef.current("Recording...");
        return;
      }
      statusRef.current("Requesting microphone...");
      requestingRef.current = true;
      // Keep the existing model's capture requests by default. The native profile
      // isolates the capture change introduced with the recording/download fix.
      const nativeCapture = captureProfile === "native";
      const androidChrome = /Chrome/.test(navigator.userAgent) && /Android/.test(navigator.userAgent) && !/Edg|OPR/.test(navigator.userAgent);
      let constraints = { audio: true };
      if (nativeCapture || androidChrome) {
        constraints = { audio: {
          echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1,
          ...(!nativeCapture ? { sampleRate: 44100, sampleSize: 16 } : {}),
        } };
      }
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (generation !== generationRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      const next = new MediaRecorder(stream);
      const session = { chunks: [], blob: null, stopping: false };
      session.ready = new Promise((resolve) => { session.resolve = resolve; });
      sessionRef.current = session;
      const settings = stream.getAudioTracks?.()[0]?.getSettings?.() || {};
      console.info("Audio capture settings", {
        captureProfile: nativeCapture ? "native" : "legacy",
        echoCancellation: settings.echoCancellation ?? null,
        noiseSuppression: settings.noiseSuppression ?? null,
        autoGainControl: settings.autoGainControl ?? null,
        channelCount: settings.channelCount ?? null,
        sampleRate: settings.sampleRate ?? null,
        sampleSize: settings.sampleSize ?? null,
        mimeType: next.mimeType,
        audioBitsPerSecond: next.audioBitsPerSecond ?? null,
      });
      recorderRef.current = next;
      next.ondataavailable = (event) => {
        if (event.data.size > 0) session.chunks.push(event.data);
      };
      next.onstop = () => {
        session.blob = new Blob(session.chunks, { type: next.mimeType || "audio/webm" });
        session.resolve(session.blob);
        setAudioBlob(session.blob);
        setRecording(false);
        release();
        statusRef.current("Recording ready ✓");
      };
      next.start();
      setRecordTime(0);
      setUploadedAudio(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setAudioBlob(true);
      setRecorded(true);
      setRecording(true);
      startTimer();
      statusRef.current("Recording...");
    } catch (error) {
      if (generation === generationRef.current) {
        release();
        statusRef.current("Microphone unavailable");
      }
    } finally {
      requestingRef.current = false;
    }
  };

  const getAudio = async () => {
    if (requestingRef.current) return null;
    if (uploadedAudio) return uploadedAudio;
    const recorder = recorderRef.current;
    if (!recorder) return audioBlob instanceof Blob ? audioBlob : null;
    const session = sessionRef.current;
    if (!session.stopping && recorder.state !== "inactive") {
      session.stopping = true;
      clearInterval(timerRef.current);
      setRecording(false);
      statusRef.current("Finishing recording...");
      try {
        recorder.stop();
      } catch (error) {
        dispose();
        throw error;
      }
    }
    // stop() changes state before final data/stop events. Even an inactive
    // recorder must wait for those events; every consumer receives one blob.
    return session.ready;
  };

  const handleAudioUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    dispose();
    setRecording(false);
    setRecorded(false);
    setRecordTime(0);
    setUploadedAudio(file);
    setAudioBlob(file);
    statusRef.current("Audio file selected");
  };

  const handleClearUploadedAudio = () => {
    setUploadedAudio(null);
    setAudioBlob(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    statusRef.current("Audio cleared");
  };

  return { recording, recorded, recordTime, audioBlob, uploadedAudio, fileInputRef, reset, getAudio,
    handleRecordClick, handleAudioUpload, handleClearUploadedAudio };
}
